import { useState } from 'react';

import { ControllerRenderProps, UseFormReturn } from 'react-hook-form';

import { BasicInfoForm } from './schema';
import { MatterLanguage } from '../../../../../../../backend/src/types/types';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { NewChip } from '@/new/components/new-chip';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { TooltipIcon } from '@/new/components/tooltip-icon';

export const MATTER_LANGUAGE_LABELS: Record<MatterLanguage, string> = {
  [MatterLanguage.EnglishUK]: 'English (UK)',
  [MatterLanguage.EnglishUS]: 'English (US)',
  [MatterLanguage.French]: 'French',
  [MatterLanguage.German]: 'German',
  [MatterLanguage.Dutch]: 'Dutch',
  [MatterLanguage.Italian]: 'Italian',
};

const MATTER_LANGUAGE_TOOLTIP =
  "This is the language this Matter's documents will be processed in. Facts, inconsistencies, and entities will be generated in this language.";

interface MatterLanguageSelectProps {
  field: ControllerRenderProps<BasicInfoForm, 'language'>;
}

function MatterLanguageSelect({ field }: MatterLanguageSelectProps) {
  const currentLanguage = field.value ?? MatterLanguage.EnglishUK;
  const [pendingLanguage, setPendingLanguage] = useState<MatterLanguage | null>(null);

  const handleValueChange = (value: string) => {
    const nextLanguage = value as MatterLanguage;
    if (nextLanguage === currentLanguage) return;
    // Confirm before applying: the language drives how every downstream output is generated.
    setPendingLanguage(nextLanguage);
  };

  const confirmChange = () => {
    if (pendingLanguage) {
      field.onChange(pendingLanguage);
    }
    setPendingLanguage(null);
  };

  return (
    <>
      <Select onValueChange={handleValueChange} value={currentLanguage}>
        <FormControl>
          <SelectTrigger data-testid="matter-creator-language-field">
            <SelectValue placeholder="Select a language" />
          </SelectTrigger>
        </FormControl>
        <SelectContent>
          {Object.values(MatterLanguage).map((language) => (
            <SelectItem key={language} value={language}>
              {MATTER_LANGUAGE_LABELS[language]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <ConfirmationModal
        isOpen={pendingLanguage !== null}
        onOpenChange={(open) => {
          if (!open) setPendingLanguage(null);
        }}
        title="Change Matter Language?"
        confirmLabel="Confirm"
        onConfirm={confirmChange}
        isLoading={false}
      >
        <p className="text-sm text-gray-700">
          This Matter's documents will be processed in{' '}
          <span className="font-medium">{pendingLanguage ? MATTER_LANGUAGE_LABELS[pendingLanguage] : ''}</span>. Facts,
          inconsistencies, and entities will be generated in this language.
        </p>
      </ConfirmationModal>
    </>
  );
}

interface BasicInfoStepProps {
  form: UseFormReturn<BasicInfoForm>;
  requireCode: boolean;
  showLanguage: boolean;
}

export function MatterCreatorBasic({ form, requireCode, showLanguage }: BasicInfoStepProps) {
  return (
    <div className="mx-auto w-full max-w-3xl px-3 py-6">
      <div className="my-8">
        <h2 className="font-serif text-xl font-medium">Basic Matter Information</h2>
      </div>

      <div className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormMessage />
              <FormLabel required>Matter Name</FormLabel>
              <p className="-mt-0.5 mb-3 text-xs whitespace-normal text-gray-500">This can be updated later.</p>
              <FormControl>
                <Input {...field} placeholder="Company A vs Company B" data-testid="matter-creator-name-field" />
              </FormControl>
            </FormItem>
          )}
        />

        {showLanguage && (
          <FormField
            control={form.control}
            name="language"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <span>Matter Language</span>
                  <TooltipIcon side="top" tooltipContent={MATTER_LANGUAGE_TOOLTIP} triggerClassName="h-3.5 w-3.5" />
                  <NewChip className="relative top-auto right-auto ml-1 py-0.5" />
                </FormLabel>
                <p className="-mt-0.5 mb-3 text-xs whitespace-normal text-gray-500">
                  The primary language of this Matter. This can only be set while the Matter is a draft.
                </p>
                <MatterLanguageSelect field={field} />
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <hr className="my-10" />

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              {requireCode ? (
                <FormLabel required>Matter Code</FormLabel>
              ) : (
                <FormLabel>
                  Matter Code <span className="font-normal text-gray-400">Optional</span>
                </FormLabel>
              )}
              <FormControl>
                <Input {...field} placeholder="WEX-123" data-testid="matter-creator-code-field" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="tokenLimit"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Matter Page Limit <span className="font-normal text-gray-400">Optional</span>
                <p className="mt-2 text-xs whitespace-normal text-gray-500">
                  The maximum number of pages that can be processed in this Matter before always requiring approval.
                </p>
              </FormLabel>

              <FormControl>
                <Input
                  {...field}
                  type="number"
                  data-testid="matter-creator-token-limit-field"
                  min="0"
                  step="1000"
                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
